/**
 * User Invite Page
 *
 * Form for inviting a new user to the tenant with role and environment assignments.
 * Capability-gated to users with admin:users capability.
 */

import { useMutation } from "@tanstack/react-query";
import type { Environment } from "@tatvex/types";
import { ArrowLeft, Send, Loader2, Check } from "lucide-react";
import type { ReactElement, FormEvent, ChangeEvent } from "react";
import { useState, useCallback } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { apiRequest } from "../../../api/client";
import { useAuthStore } from "../../../store/authStore";

import type { UserInviteInput, TenantUserView } from "./types";
import { SYSTEM_ROLE_INFO, ENVIRONMENT_INFO, ALL_ENVIRONMENTS, SYSTEM_ROLES } from "./types";

interface FormState {
  email: string;
  name: string;
  roles: string[];
  environments: Environment[];
}

export function UserInvitePage(): ReactElement {
  const { hasCapability } = useAuthStore();

  // Check capability
  if (!hasCapability("admin:users")) {
    return <Navigate to="/studio" replace />;
  }

  return <UserInviteContent />;
}

function UserInviteContent(): ReactElement {
  const navigate = useNavigate();
  const [formState, setFormState] = useState<FormState>({
    email: "",
    name: "",
    roles: ["viewer"],
    environments: ["dev"],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const inviteMutation = useMutation({
    mutationFn: async (input: UserInviteInput) => {
      const response = await apiRequest<TenantUserView>("/api/v1/admin/users", {
        method: "POST",
        body: input,
      });

      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: (user) => {
      navigate(`/studio/admin/users/${user.externalId}`);
    },
  });

  const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
    setFormState((prev) => ({ ...prev, email: e.target.value }));
    setErrors((prev) => ({ ...prev, ["email"]: "" }));
  }, []);

  const handleNameChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
    setFormState((prev) => ({ ...prev, name: e.target.value }));
    setErrors((prev) => ({ ...prev, ["name"]: "" }));
  }, []);

  const handleRoleToggle = useCallback((roleId: string): void => {
    setFormState((prev) => ({
      ...prev,
      roles: prev.roles.includes(roleId)
        ? prev.roles.filter((r) => r !== roleId)
        : [...prev.roles, roleId],
    }));
    setErrors((prev) => ({ ...prev, ["roles"]: "" }));
  }, []);

  const handleEnvironmentToggle = useCallback((env: Environment): void => {
    setFormState((prev) => ({
      ...prev,
      environments: prev.environments.includes(env)
        ? prev.environments.filter((e) => e !== env)
        : [...prev.environments, env],
    }));
    setErrors((prev) => ({ ...prev, ["environments"]: "" }));
  }, []);

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formState.email.trim()) {
      newErrors["email"] = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      newErrors["email"] = "Please enter a valid email address";
    }

    if (!formState.name.trim()) {
      newErrors["name"] = "Name is required";
    } else if (formState.name.length < 2) {
      newErrors["name"] = "Name must be at least 2 characters";
    } else if (formState.name.length > 100) {
      newErrors["name"] = "Name must be less than 100 characters";
    }

    if (formState.roles.length === 0) {
      newErrors["roles"] = "At least one role is required";
    }

    if (formState.environments.length === 0) {
      newErrors["environments"] = "At least one environment is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formState]);

  const handleSubmit = useCallback(
    (e: FormEvent): void => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      const input: UserInviteInput = {
        email: formState.email.trim().toLowerCase(),
        name: formState.name.trim(),
        roles: formState.roles,
        environments: formState.environments,
      };

      inviteMutation.mutate(input);
    },
    [formState, validateForm, inviteMutation]
  );

  return (
    <div className="p-6 max-w-2xl">
      {/* Page header */}
      <div className="mb-6">
        <Link
          to="/studio/admin/users"
          className="inline-flex items-center gap-1 text-sm text-foreground-muted hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </Link>
        <h1 className="text-2xl font-bold">Invite User</h1>
        <p className="mt-1 text-foreground-muted">
          Send an invitation to a new team member with assigned roles and environment access
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">User Information</h2>
          <div className="space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1">
                Email Address <span className="text-error">*</span>
              </label>
              <input
                id="email"
                type="email"
                value={formState.email}
                onChange={handleEmailChange}
                placeholder="user@example.com"
                className={`w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                  errors["email"]
                    ? "border-error focus:border-error focus:ring-error"
                    : "border-border focus:border-primary focus:ring-primary"
                }`}
              />
              <p className="mt-1 text-xs text-foreground-muted">
                An invitation email will be sent to this address
              </p>
              {errors["email"] && <p className="mt-1 text-sm text-error">{errors["email"]}</p>}
            </div>

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-1">
                Full Name <span className="text-error">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={formState.name}
                onChange={handleNameChange}
                placeholder="John Doe"
                className={`w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                  errors["name"]
                    ? "border-error focus:border-error focus:ring-error"
                    : "border-border focus:border-primary focus:ring-primary"
                }`}
              />
              {errors["name"] && <p className="mt-1 text-sm text-error">{errors["name"]}</p>}
            </div>
          </div>
        </div>

        {/* Roles */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-2">Roles</h2>
          <p className="text-sm text-foreground-muted mb-4">
            Select the roles to assign. At least one role is required.
          </p>
          {errors["roles"] && <p className="mb-3 text-sm text-error">{errors["roles"]}</p>}
          <div className="space-y-3">
            {SYSTEM_ROLES.map((roleId) => {
              const roleInfo = SYSTEM_ROLE_INFO[roleId];
              const isSelected = formState.roles.includes(roleId);

              return (
                <label
                  key={roleId}
                  className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleRoleToggle(roleId)}
                    className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <div className="flex-1">
                    <p className="font-medium">{roleInfo?.label ?? roleId}</p>
                    <p className="text-sm text-foreground-muted">
                      {roleInfo?.description ?? "Custom role"}
                    </p>
                  </div>
                  {isSelected && <Check className="h-5 w-5 text-primary" />}
                </label>
              );
            })}
          </div>
        </div>

        {/* Environments */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-2">Environment Access</h2>
          <p className="text-sm text-foreground-muted mb-4">
            Select which environments this user can access. Production access should be limited.
          </p>
          {errors["environments"] && (
            <p className="mb-3 text-sm text-error">{errors["environments"]}</p>
          )}
          <div className="grid gap-3 sm:grid-cols-3">
            {ALL_ENVIRONMENTS.map((env) => {
              const envInfo = ENVIRONMENT_INFO[env];
              const isSelected = formState.environments.includes(env);

              return (
                <label
                  key={env}
                  className={`flex items-center gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleEnvironmentToggle(env)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <div className="flex-1">
                    <p className="font-medium">{envInfo.label}</p>
                    <p className="text-xs text-foreground-muted">{envInfo.description}</p>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Error message */}
        {inviteMutation.isError && (
          <div className="rounded-md border border-error bg-error/10 p-4">
            <p className="text-sm text-error">
              {inviteMutation.error instanceof Error
                ? inviteMutation.error.message
                : "Failed to send invitation"}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Link to="/studio/admin/users" className="btn btn-secondary">
            Cancel
          </Link>
          <button type="submit" disabled={inviteMutation.isPending} className="btn btn-primary">
            {inviteMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Send Invitation
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
